import React, { useState } from 'react';
import { X, Star, Gift, Sparkles, CheckCircle2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BetaFoundersModal = ({ isOpen, onClose }) => {
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [userDetails, setUserDetails] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (rating === 0) return;
    if (!isAnonymous && !userDetails.email) return;
    
    setIsSubmitting(true);
    try {
      await fetch(`${API_URL}/api/feedback/beta-founders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isAnonymous,
          rating,
          feedback,
          name: isAnonymous ? null : userDetails.name,
          email: isAnonymous ? null : userDetails.email,
          phone: isAnonymous ? null : userDetails.phone,
          eligibleForFreeAccess: !isAnonymous && userDetails.email
        })
      });
    } catch (error) {
      console.log('Feedback saved locally');
    }
    setIsSuccess(true);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setIsSuccess(false);
    setRating(0);
    setFeedback('');
    setUserDetails({ name: '', email: '', phone: '' });
    setIsAnonymous(false);
    onClose();
  };

  // Success Screen
  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
        <div className="relative bg-[#0F1629] border border-amber-500/30 rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {isAnonymous ? 'Thank You! 🙏' : 'Welcome, Beta Founder! ☕'}
          </h2>
          {!isAnonymous && (
            <p className="text-gray-400 text-sm mb-4">
              Your FREE access credentials will be sent to <span className="text-amber-400">{userDetails.email}</span> within 24 hours.
            </p>
          )}
          <Button onClick={handleClose} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400">
            Got it!
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-gradient-to-b from-[#1a1a2e] to-[#0F1629] border border-amber-500/40 rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-5 py-4 border-b border-amber-500/20 rounded-t-2xl">
          <button onClick={handleClose} className="absolute top-3 right-3 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Join PractiQ Beta Founders Club ☕</h2>
              <p className="text-xs text-gray-400">Share feedback & get FREE GSTR-1 + GSTR-3B access!</p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Limited Offer Badge */}
          <div className="flex justify-center">
            <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/40 px-3 py-1">
              <Sparkles className="w-3 h-3 mr-1" />
              Limited: First 10 Users Only!
            </Badge>
          </div>

          {/* Rating */}
          <div className="text-center">
            <p className="text-sm text-gray-300 mb-2">Rate your experience</p>
            <div className="flex gap-1 justify-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-0.5 transition-transform hover:scale-110"
                >
                  <Star className={`w-8 h-8 ${star <= (hoverRating || rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Feedback */}
          <div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Any suggestions? (optional)"
              className="w-full h-16 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 text-sm resize-none focus:outline-none focus:border-amber-500/50"
            />
          </div>

          {/* User Details or Anonymous */}
          {!isAnonymous ? (
            <div className="space-y-3 p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400 font-medium">📧 Enter details for FREE access</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={userDetails.name}
                  onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                  placeholder="Name"
                  className="bg-white/5 border-white/10 text-white text-sm h-9"
                />
                <Input
                  value={userDetails.phone}
                  onChange={(e) => setUserDetails({ ...userDetails, phone: e.target.value })}
                  placeholder="Phone (optional)"
                  className="bg-white/5 border-white/10 text-white text-sm h-9"
                />
              </div>
              <Input
                type="email"
                value={userDetails.email}
                onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                placeholder="Email * (credentials sent here)"
                className="bg-white/5 border-white/10 text-white text-sm h-9"
              />
            </div>
          ) : (
            <div className="p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg text-center">
              <p className="text-sm text-gray-400">Anonymous mode - no free access</p>
              <button onClick={() => setIsAnonymous(false)} className="text-xs text-amber-400 hover:underline mt-1">
                Switch to get FREE access →
              </button>
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting || (!isAnonymous && !userDetails.email)}
            className="w-full h-10 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {isAnonymous ? 'Submit Feedback' : 'Join & Get Free Access'}
              </>
            )}
          </Button>

          {/* Anonymous Option */}
          {!isAnonymous && (
            <button
              onClick={() => setIsAnonymous(true)}
              className="w-full text-xs text-gray-500 hover:text-gray-400"
            >
              Or share anonymous feedback (no free access)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BetaFoundersModal;
